<template>
  <div
    class="relative select-none"
    :class="tooltip ? 'h-[80px]' : 'h-6'"
  >
    <!-- Hidden input for form integration -->
    <input
      ref="hiddenInput"
      type="hidden"
    />

    <!-- Slider container with reactive width -->
    <div
      ref="sliderEl"
      :style="{ width: width }"
      class="relative"
    >
      <!-- Background bar -->
      <div class="absolute left-0 min-[600px]:top-[4px] top-[2px] w-full min-[600px]:h-[8px] h-3 min-[600px]:rounded-[3px] rounded-md bg-slate-400" />

      <!-- Selected range -->
      <client-only>
        <div
          ref="selectedEl"
          class="absolute left-0 min-[600px]:top-[4px] top-[2px] min-[600px]:h-[8px] h-3 min-[600px]:rounded-[3px] rounded-md bg-blue-800"
          :style="{ left: selectedLeft + 'px', width: selectedWidth + 'px' }"
        />

        <!-- Left pointer -->
        <div
          ref="pointerLeftEl"
          :style="{ left: pointerLeftPx + 'px' }"
          class="absolute min-[600px]:top-[-4.5px] top-[-7.5px] min-[600px]:w-[24px] w-8 aspect-square cursor-pointer bg-white shadow-outline rounded-full flex items-center justify-center"
          @mousedown="drag('left', $event)"
          @touchstart="drag('left', $event)"
        >
          <!-- Indicator circle (replacing ::before) -->
          <div
            class="min-[600px]:w-[9px] w-3 min-[600px]:h-[9px] h-3 rounded-full bg-blue-800"
            style="margin-right: -0.5px; margin-top: -0.5px;"
          />
          <!-- Tooltip -->
          <div
            v-if="tooltip"
            class="absolute bg-blue-800 rounded-[3px] transform -translate-x-1/2 translate-y-full left-[13px] bottom-[-8px] text-center text-[13px] px-[0.4rem] py-[0.1rem] text-white"
          >
            {{ tooltipValStart }}
          </div>
        </div>

        <!-- Right pointer (only for range mode) -->
        <div
          v-if="rangeMode"
          ref="pointerRightEl"
          :style="{ left: pointerRightPx + 'px' }"
          class="absolute min-[600px]:top-[-4.5px] top-[-7.5px] min-[600px]:w-[24px] w-8 aspect-square cursor-pointer bg-white shadow-outline rounded-full flex items-center justify-center"
          @mousedown="drag('right', $event)"
          @touchstart="drag('right', $event)"
        >
          <div
            class="min-[600px]:w-[9px] w-3 min-[600px]:h-[9px] h-3 rounded-full bg-blue-800"
            style="margin-right: -0.5px; margin-top: -0.5px;"
          />
          <div
            v-if="tooltip"
            class="absolute bg-blue-800 rounded-[3px] transform -translate-x-1/2 translate-y-full left-[13px] bottom-[-8px] text-center text-[13px] px-[0.4rem] py-[0.1rem] text-white"
          >
            {{ tooltipValEnd }}
          </div>
        </div>
      </client-only>
    </div>
  </div>
</template>

<script setup>
import equal from 'fast-deep-equal'

const props = defineProps({
  value: {
    type: [Number, Array, Object],
    default: null,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  tooltip: {
    type: Boolean,
    default: true,
  },
  tooltipFormatter: {
    type: Function,
    default: null,
  },
  throttleDurationMs: {
    type: Number,
    default: 200,
  },
  width: {
    type: String,
    default: '100%',
  },
  rangeMode: {
    type: Boolean,
    default: true,
  },

  stepPointerMargin: {
    type: Number,
    default: 0, // default behavior is same as now (no margin)
  },
})

const emits = defineEmits(['input', 'move'])

const sliderEl = ref(null)
const hiddenInput = ref(null)
const pointerLeftEl = ref(null)
const pointerRightEl = ref(null)
const selectedEl = ref(null)

const sliderLeft = ref(0)
const sliderWidth = ref(0)
const stepSize = ref(0)
const pointerHalfWidth = ref(0)

const prevValue = ref([])

const rangeValues = computed(() => {
  const arr = []
  for (let v = props.min; v <= props.max; v += props.step) {
    arr.push(v)
  }
  if (arr[arr.length - 1] !== props.max) {
    arr.push(props.max)
  }
  return arr
})

const startIndex = ref(0)
const endIndex = ref(rangeValues.value.length - 1)
// Use provided prop value if given (expects an array)
if (props.value && Array.isArray(props.value)) {
  const initial = props.value
  const leftIdx = rangeValues.value.indexOf(Number(initial[0]))
  const rightIdx = rangeValues.value.indexOf(Number(initial[1]))
  if (leftIdx !== -1) {
    startIndex.value = leftIdx
  }
  if (rightIdx !== -1) {
    endIndex.value = rightIdx
  }
}

const pointerLeftPx = computed(() => startIndex.value * stepSize.value - pointerHalfWidth.value)
const pointerRightPx = computed(() => endIndex.value * stepSize.value - pointerHalfWidth.value)
const selectedLeft = computed(() => startIndex.value * stepSize.value)
const selectedWidth = computed(() => (endIndex.value - startIndex.value) * stepSize.value)

const tooltipValStart = computed(() => {
  const val = rangeValues.value[startIndex.value]
  return props.tooltipFormatter ? props.tooltipFormatter(val) : val
})
const tooltipValEnd = computed(() => {
  const val = rangeValues.value[endIndex.value]
  return props.tooltipFormatter ? props.tooltipFormatter(val) : val
})

const activePointer = ref(null) // "left" or "right"

const drag = (pointer, event) => {
  event.preventDefault()
  activePointer.value = pointer
  document.addEventListener('mousemove', onDragMove)
  document.addEventListener('touchmove', onDragMove)
  document.addEventListener('mouseup', onDragEnd)
  document.addEventListener('touchend', onDragEnd)
}

const onDragMove = (e) => {
  if (!activePointer.value) {
    return
  }

  const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.pageX
  let index = Math.round((clientX - sliderLeft.value - pointerHalfWidth.value) / stepSize.value)
  index = Math.max(0, Math.min(index, rangeValues.value.length - 1))

  const margin = props.stepPointerMargin

  if (activePointer.value === 'left') {
    // Enforce boundary limit for pushing
    if (props.rangeMode) {
      if (index > endIndex.value - margin) {
        const potentialRight = index + margin
        if (potentialRight <= rangeValues.value.length - 1) {
          startIndex.value = index
          endIndex.value = potentialRight
        } else {
          // Right pointer can't move further — stop left pointer too
          startIndex.value = rangeValues.value.length - 1 - margin
          endIndex.value = rangeValues.value.length - 1
        }
      } else {
        startIndex.value = index
      }
    } else {
      startIndex.value = index
    }
  } else if (activePointer.value === 'right') {
    if (index < startIndex.value + margin) {
      const potentialLeft = index - margin
      if (potentialLeft >= 0) {
        endIndex.value = index
        startIndex.value = potentialLeft
      } else {
        // Left pointer can't move further — stop right pointer too
        startIndex.value = 0
        endIndex.value = margin
      }
    } else {
      endIndex.value = index
    }
  }

  const newValue = props.rangeMode
    ? [rangeValues.value[startIndex.value], rangeValues.value[endIndex.value]]
    : [rangeValues.value[endIndex.value]]

  if (!equal(newValue, prevValue.value)) {
    emits('move', newValue)
    prevValue.value = newValue
  }

  emitChange()
}

const onDragEnd = () => {
  activePointer.value = null
  document.removeEventListener('mousemove', onDragMove)
  document.removeEventListener('touchmove', onDragMove)
  document.removeEventListener('mouseup', onDragEnd)
  document.removeEventListener('touchend', onDragEnd)
  emitChange()
}

let changeTimeout = null
const emitChange = () => {
  if (changeTimeout) {
    clearTimeout(changeTimeout)
  }
  changeTimeout = setTimeout(() => {
    const newValue = props.rangeMode
      ? [rangeValues.value[startIndex.value], rangeValues.value[endIndex.value]]
      : [rangeValues.value[endIndex.value]]
    emits('input', newValue)
  }, props.throttleDurationMs)
}

const updateDimensions = () => {
  if (sliderEl.value) {
    const rect = sliderEl.value.getBoundingClientRect()
    sliderLeft.value = rect.left
    sliderWidth.value = sliderEl.value.clientWidth
    if (rangeValues.value.length > 1) {
      stepSize.value = sliderWidth.value / (rangeValues.value.length - 1)
    }
  }
  if (pointerLeftEl.value) {
    pointerHalfWidth.value = pointerLeftEl.value.clientWidth / 2
  }
}

watch(() => props.value, () => {
  if (props.value && Array.isArray(props.value)) {
    const initial = props.value
    const leftIdx = rangeValues.value.indexOf(Number(initial[0]))
    const rightIdx = rangeValues.value.indexOf(Number(initial[1]))

    startIndex.value = leftIdx !== -1
      ? leftIdx
      : props.min
    endIndex.value = rightIdx !== -1
      ? rightIdx
      : props.max - 1
  }
})

onMounted(() => {
  nextTick(() => {
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
  })
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDimensions)
})
</script>